import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaCar, FaCalendarAlt, FaChevronDown, FaGasPump, FaDollarSign, FaCheckCircle, FaChartLine, FaEdit, FaWrench, FaPlus, FaSearch, FaUndo, FaTimes } from 'react-icons/fa';
import {
  fetchAllTotalEnergiesInvoices,
  createTotalEnergiesInvoice,
  calcInvoiceTotal,
  getTotalEnergiesInvoiceErrorMessage,
} from '../services/totalEnergiesInvoicesService';
import './BTSProjectPage.css';

const RETURNED_VEHICLES_STORAGE_KEY = 'bts_returned_vehicles';
const BTS_PROJECT = 'BTS';

const loadReturnedVehiclesFromStorage = () => {
  try {
    const stored = localStorage.getItem(RETURNED_VEHICLES_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading returned vehicles from storage:', error);
  }
  return null;
};

const saveReturnedVehiclesToStorage = (vehicles) => {
  try {
    localStorage.setItem(RETURNED_VEHICLES_STORAGE_KEY, JSON.stringify(vehicles));
  } catch (error) {
    console.error('Error saving returned vehicles to storage:', error);
  }
};

function BTSProjectPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [maintenanceSubTab, setMaintenanceSubTab] = useState('record'); // 'record' or 'returned'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [selectedCarRental, setSelectedCarRental] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedVehicleForReturn, setSelectedVehicleForReturn] = useState(null);
  const [returnDate, setReturnDate] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  
  // Returned Vehicles State - Initialize from localStorage or use defaults
  const defaultReturnedVehicles = [
    { id: 1, regNo: 'T123ABC', vehicleType: 'Sedan', carRental: 'Hertz', vehicleUser: 'John Doe', returnDate: '2025-08-20', returnReason: 'End of project assignment', status: 'Returned' },
    { id: 2, regNo: 'T456DEF', vehicleType: 'SUV', carRental: 'Avis', vehicleUser: 'Jane Smith', returnDate: '2025-08-15', returnReason: 'Vehicle maintenance required', status: 'Returned' },
    { id: 3, regNo: 'T789GHI', vehicleType: 'Pickup', carRental: 'Budget', vehicleUser: 'Mike Johnson', returnDate: '2025-08-10', returnReason: 'Contract completion', status: 'Returned' },
  ];

  const [returnedVehicles, setReturnedVehicles] = useState(() => {
    const stored = loadReturnedVehiclesFromStorage();
    return stored || defaultReturnedVehicles;
  });

  // Save returned vehicles to localStorage whenever they change
  useEffect(() => {
    saveReturnedVehiclesToStorage(returnedVehicles);
  }, [returnedVehicles]);
  
  // PO Tracking Data
  const [poTracking, setPOTracking] = useState([
    { id: 1, poNumber: '660003077-2', project: 'BTS', poAmount: 25000000, totalAmountInvoiced: 21808255, comment: 'Pending invoice for August 2025' },
    { id: 2, poNumber: '660003077', project: 'BTS', poAmount: 20000000, totalAmountInvoiced: 18634786, comment: 'July 2025 invoice paid' },
    { id: 3, poNumber: '660003077', project: 'BTS', poAmount: 20000000, totalAmountInvoiced: 19369443, comment: 'June 2025 invoice paid' },
    { id: 4, poNumber: '660003077', project: 'BTS', poAmount: 22000000, totalAmountInvoiced: 20286677, comment: 'May 2025 invoice paid' },
    { id: 5, poNumber: '660002932', project: 'BTS', poAmount: 20000000, totalAmountInvoiced: 19024681, comment: 'April 2025 invoice paid' },
    { id: 6, poNumber: '660002932', project: 'BTS', poAmount: 18000000, totalAmountInvoiced: 17578392, comment: 'March 2025 invoice paid' },
    { id: 7, poNumber: '660002932', project: 'BTS', poAmount: 17000000, totalAmountInvoiced: 16348166, comment: 'February 2025 invoice paid' },
    { id: 8, poNumber: '660002932', project: 'BTS', poAmount: 21000000, totalAmountInvoiced: 20015670, comment: 'January 2025 invoice paid' },
  ]);

  // PO Modal State
  const [showPOModal, setShowPOModal] = useState(false);
  const [poForm, setPOForm] = useState({
    poNumber: '',
    project: 'BTS',
    poAmount: '',
    totalAmountInvoiced: '',
    comment: ''
  });

  // Invoice Tracking Data
  const [invoiceTracking, setInvoiceTracking] = useState([
    { id: 1, invoiceNumber: 'INV-2025-001', poNumber: '660003077-2', project: 'BTS', invoiceAmount: 21808255, dateInvoiced: '2025-08-15', paymentStatus: 'Pending', datePaid: '', comment: 'August 2025 invoice' },
    { id: 2, invoiceNumber: 'INV-2025-002', poNumber: '660003077', project: 'BTS', invoiceAmount: 18634786, dateInvoiced: '2025-07-10', paymentStatus: 'Paid', datePaid: '2025-07-25', comment: 'July 2025 invoice paid' },
    { id: 3, invoiceNumber: 'INV-2025-003', poNumber: '660003077', project: 'BTS', invoiceAmount: 19369443, dateInvoiced: '2025-06-12', paymentStatus: 'Paid', datePaid: '2025-06-28', comment: 'June 2025 invoice paid' },
    { id: 4, invoiceNumber: 'INV-2025-004', poNumber: '660003077', project: 'BTS', invoiceAmount: 20286677, dateInvoiced: '2025-05-08', paymentStatus: 'Paid', datePaid: '2025-05-22', comment: 'May 2025 invoice paid' },
  ]);

  // Invoice Tracking Modal State
  const [showInvoiceTrackingModal, setShowInvoiceTrackingModal] = useState(false);
  const [invoiceTrackingForm, setInvoiceTrackingForm] = useState({
    invoiceNumber: '',
    poNumber: '',
    project: 'BTS',
    invoiceAmount: '',
    dateInvoiced: '',
    paymentStatus: 'Pending',
    datePaid: '',
    comment: ''
  });

  // Handle opening PO modal
  const handleOpenPOModal = () => {
    setPOForm({
      poNumber: '',
      project: 'BTS',
      poAmount: '',
      totalAmountInvoiced: '',
      comment: ''
    });
    setShowPOModal(true);
  };

  // Handle closing PO modal
  const handleClosePOModal = () => {
    setShowPOModal(false);
    setPOForm({
      poNumber: '',
      project: 'BTS',
      poAmount: '',
      totalAmountInvoiced: '',
      comment: ''
    });
  };

  // Handle PO form change
  const handlePOFormChange = (field, value) => {
    setPOForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle submitting new PO
  const handleSubmitPO = () => {
    // Validate required fields
    if (!poForm.poNumber || !poForm.poAmount) {
      alert('Please fill in all required fields (PO Number and PO Amount).');
      return;
    }

    // Parse amounts
    const poAmount = parseTZS(poForm.poAmount);
    const totalAmountInvoiced = parseTZS(poForm.totalAmountInvoiced || '0');

    // Generate unique ID
    const maxId = poTracking.length > 0 ? Math.max(...poTracking.map(po => po.id)) : 0;

    // Create new PO
    const newPO = {
      id: maxId + 1,
      poNumber: poForm.poNumber,
      project: poForm.project,
      poAmount: poAmount,
      totalAmountInvoiced: totalAmountInvoiced,
      comment: poForm.comment || ''
    };

    // Add to PO tracking list (add at the beginning to show newest first)
    setPOTracking(prev => [newPO, ...prev]);
    
    alert(`PO ${newPO.poNumber} has been recorded successfully!`);
    handleClosePOModal();
  };

  // Handle opening Invoice Tracking modal
  const handleOpenInvoiceTrackingModal = () => {
    // Get available PO numbers from poTracking for dropdown
    setInvoiceTrackingForm({
      invoiceNumber: '',
      poNumber: '',
      project: 'BTS',
      invoiceAmount: '',
      dateInvoiced: new Date().toISOString().split('T')[0], // Today's date
      paymentStatus: 'Pending',
      datePaid: '',
      comment: ''
    });
    setShowInvoiceTrackingModal(true);
  };

  // Handle closing Invoice Tracking modal
  const handleCloseInvoiceTrackingModal = () => {
    setShowInvoiceTrackingModal(false);
    setInvoiceTrackingForm({
      invoiceNumber: '',
      poNumber: '',
      project: 'BTS',
      invoiceAmount: '',
      dateInvoiced: '',
      paymentStatus: 'Pending',
      datePaid: '',
      comment: ''
    });
  };

  // Handle Invoice Tracking form change
  const handleInvoiceTrackingFormChange = (field, value) => {
    // If payment status changes to "Paid" and datePaid is empty, set today's date
    if (field === 'paymentStatus' && value === 'Paid' && !invoiceTrackingForm.datePaid) {
      setInvoiceTrackingForm(prev => ({ ...prev, [field]: value, datePaid: new Date().toISOString().split('T')[0] }));
    } else {
      setInvoiceTrackingForm(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle submitting new Invoice Tracking record
  const handleSubmitInvoiceTracking = () => {
    // Validate required fields
    if (!invoiceTrackingForm.invoiceNumber || !invoiceTrackingForm.poNumber || !invoiceTrackingForm.invoiceAmount) {
      alert('Please fill in all required fields (Invoice Number, PO Number, and Invoice Amount).');
      return;
    }

    // Parse amount
    const invoiceAmount = parseTZS(invoiceTrackingForm.invoiceAmount);

    // Generate unique ID
    const maxId = invoiceTracking.length > 0 ? Math.max(...invoiceTracking.map(inv => inv.id)) : 0;

    // Create new invoice tracking record
    const newInvoice = {
      id: maxId + 1,
      invoiceNumber: invoiceTrackingForm.invoiceNumber,
      poNumber: invoiceTrackingForm.poNumber,
      project: invoiceTrackingForm.project,
      invoiceAmount: invoiceAmount,
      dateInvoiced: invoiceTrackingForm.dateInvoiced,
      paymentStatus: invoiceTrackingForm.paymentStatus,
      datePaid: invoiceTrackingForm.paymentStatus === 'Paid' ? invoiceTrackingForm.datePaid : '',
      comment: invoiceTrackingForm.comment || ''
    };

    // Add to invoice tracking list (add at the beginning to show newest first)
    setInvoiceTracking(prev => [newInvoice, ...prev]);
    
    // Update PO tracking: add invoice amount to totalAmountInvoiced for matching PO
    setPOTracking(prev => prev.map(po => {
      if (po.poNumber === invoiceTrackingForm.poNumber) {
        return {
          ...po,
          totalAmountInvoiced: po.totalAmountInvoiced + invoiceAmount
        };
      }
      return po;
    }));
    
    alert(`Invoice ${newInvoice.invoiceNumber} has been recorded successfully!`);
    handleCloseInvoiceTrackingModal();
  };
  
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [, setInvoicesError] = useState('');
  const [, setInvoiceSubmitting] = useState(false);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoicesError('');
    try {
      const rows = await fetchAllTotalEnergiesInvoices();
      setInvoices(rows);
    } catch (err) {
      console.error(err);
      setInvoices([]);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(err) || 'Could not load fuel invoices.');
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const btsInvoices = useMemo(
    () => invoices.filter((inv) => String(inv.project || '').trim() === BTS_PROJECT),
    [invoices]
  );

  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNo: '',
    invoiceDate: '',
    project: 'BTS',
    fuelAmount: '',
    managementFee: '',
    vat: '',
    po: '',
    paymentStatus: 'Pending',
    datePaid: '',
    comment: ''
  });

  const formatNumber = (amount) => {
    const safe = Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US').format(Math.round(safe));
  };

  const formatTZS = (amount) => {
    const safe = Number.isFinite(amount) ? amount : 0;
    return `TZS ${new Intl.NumberFormat('en-US').format(Math.round(safe))}`;
  };

  const parseTZS = (value) => {
    if (value === null || value === undefined) return 0;
    const s = String(value);
    const numeric = s.replace(/[^0-9.]/g, '');
    const n = Number(numeric);
    return Number.isFinite(n) ? n : 0;
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const s = String(value);
    const numeric = s.replace(/[^0-9.]/g, '');
    const n = Number(numeric);
    return Number.isFinite(n) ? n : 0;
  };

  // Compute current month/year label, e.g., "January 2026"
  const currentMonthYearLabel = useMemo(() => {
    const monthNames = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    const now = new Date();
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  // Vehicle report data (initial)
  const initialVehicleRows = useMemo(() => {
    const monthNames = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];

    // December 2025 vehicle templates (excluding sample vehicle)
    const december2025Vehicles = [
      { vehicleType: 'SUV', carRental: 'Smiles', regNo: 'T249EFF', vehicleUser: 'PALLANGYO Kelvin', daysUsed: '25', rentalCost: 'TZS 2,500,000', rentalCostThisMonth: 'TZS 2,500,000', startingMileage: '45,230', endingMileage: '48,750', kmsTraveled: '3,520', fuel: '350', fuelCost: 'TZS 1,050,000', fuelConsumption: '9.9 L/100km' },
      { vehicleType: 'Pick Up', carRental: 'Satguru', regNo: 'T455EFG', vehicleUser: 'MOSHA Henry', daysUsed: '28', rentalCost: 'TZS 2,800,000', rentalCostThisMonth: 'TZS 2,800,000', startingMileage: '32,100', endingMileage: '35,890', kmsTraveled: '3,790', fuel: '380', fuelCost: 'TZS 1,140,000', fuelConsumption: '10.0 L/100km' },
      { vehicleType: 'Pick Up', carRental: 'Master one', regNo: 'T149DQK', vehicleUser: 'JUMAA Ally', daysUsed: '22', rentalCost: 'TZS 2,200,000', rentalCostThisMonth: 'TZS 2,200,000', startingMileage: '67,450', endingMileage: '70,120', kmsTraveled: '2,670', fuel: '280', fuelCost: 'TZS 840,000', fuelConsumption: '10.5 L/100km' },
      { vehicleType: 'SUV', carRental: 'Smiles', regNo: 'T375DUM', vehicleUser: 'MUGARULA Laizack', daysUsed: '30', rentalCost: 'TZS 3,000,000', rentalCostThisMonth: 'TZS 3,000,000', startingMileage: '28,900', endingMileage: '33,200', kmsTraveled: '4,300', fuel: '420', fuelCost: 'TZS 1,260,000', fuelConsumption: '9.8 L/100km' },
      { vehicleType: 'Pick Up', carRental: 'Satguru', regNo: 'T456EEL', vehicleUser: 'BAKTACHE Madani', daysUsed: '20', rentalCost: 'TZS 2,000,000', rentalCostThisMonth: 'TZS 2,000,000', startingMileage: '52,300', endingMileage: '54,800', kmsTraveled: '2,500', fuel: '250', fuelCost: 'TZS 750,000', fuelConsumption: '10.0 L/100km' },
      { vehicleType: 'SUV', carRental: 'Master one', regNo: 'T155DQL', vehicleUser: 'CHUWA Aloyce', daysUsed: '26', rentalCost: 'TZS 2,600,000', rentalCostThisMonth: 'TZS 2,600,000', startingMileage: '38,500', endingMileage: '41,200', kmsTraveled: '2,700', fuel: '320', fuelCost: 'TZS 960,000', fuelConsumption: '11.9 L/100km' },
      { vehicleType: 'Pick Up', carRental: 'Smiles', regNo: 'T923DWW', vehicleUser: 'Evance', daysUsed: '24', rentalCost: 'TZS 2,400,000', rentalCostThisMonth: 'TZS 2,400,000', startingMileage: '55,600', endingMileage: '58,900', kmsTraveled: '3,300', fuel: '330', fuelCost: 'TZS 990,000', fuelConsumption: '10.0 L/100km' },
      { vehicleType: 'SUV', carRental: 'Satguru', regNo: 'T423ENE', vehicleUser: 'MLAY Davis', daysUsed: '27', rentalCost: 'TZS 2,700,000', rentalCostThisMonth: 'TZS 2,700,000', startingMileage: '42,300', endingMileage: '45,800', kmsTraveled: '3,500', fuel: '360', fuelCost: 'TZS 1,080,000', fuelConsumption: '10.3 L/100km' },
      { vehicleType: 'Pick Up', carRental: 'Master one', regNo: 'T476DUQ', vehicleUser: 'NELSON CHALLI', daysUsed: '23', rentalCost: 'TZS 2,300,000', rentalCostThisMonth: 'TZS 2,300,000', startingMileage: '49,200', endingMileage: '52,100', kmsTraveled: '2,900', fuel: '290', fuelCost: 'TZS 870,000', fuelConsumption: '10.0 L/100km' },
      { vehicleType: 'SUV', carRental: 'Smiles', regNo: 'T106DQB', vehicleUser: 'Kerenge Andrew', daysUsed: '29', rentalCost: 'TZS 2,900,000', rentalCostThisMonth: 'TZS 2,900,000', startingMileage: '36,800', endingMileage: '40,500', kmsTraveled: '3,700', fuel: '370', fuelCost: 'TZS 1,110,000', fuelConsumption: '10.0 L/100km' },
    ];

    // Generate months from January 2025 to current month
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const allMonths = [];

    // Start from January 2025
    for (let year = 2025; year <= currentYear; year++) {
      const startMonth = year === 2025 ? 0 : 0; // January = 0
      const endMonth = year === currentYear ? currentMonth : 11; // December = 11

      for (let month = startMonth; month <= endMonth; month++) {
        allMonths.push(`${monthNames[month]} ${year}`);
      }
    }

    // Duplicate vehicles for each month
    const allVehicleRows = [];
    allMonths.forEach(month => {
      december2025Vehicles.forEach(vehicle => {
        allVehicleRows.push({
          ...vehicle,
          month: month
        });
      });
    });

    return allVehicleRows;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Editable rows (user can update some columns inline)
  const [vehicleRows, setVehicleRows] = useState(() => initialVehicleRows);

  const updateVehicleCell = (regNo, month, field, value) => {
    // Validate daysUsed to not exceed 31
    if (field === 'daysUsed' && value) {
      const numValue = parseNumber(value);
      if (numValue > 31) {
        return; // Don't update if value exceeds 31
      }
    }
    setVehicleRows((prev) =>
      prev.map((row) => (row.regNo === regNo && row.month === month ? { ...row, [field]: value } : row))
    );
  };

  const finalizeVehicleCell = (regNo, month, field, value) => {
    const raw = String(value ?? '');
    if (raw.trim() === '') {
      updateVehicleCell(regNo, month, field, '');
      return;
    }
    if (field === 'daysUsed') {
      const days = Math.round(parseNumber(value));
      const maxDays = 31;
      const validatedDays = Math.min(days, maxDays);
      updateVehicleCell(regNo, month, field, String(validatedDays));
      if (days > maxDays) {
        alert(`Number of days cannot exceed ${maxDays}. Value has been set to ${maxDays}.`);
      }
      return;
    }
    if (field === 'endingMileage' || field === 'fuel') {
      updateVehicleCell(regNo, month, field, formatNumber(parseNumber(value)));
      return;
    }
    if (field === 'fuelCost') {
      updateVehicleCell(regNo, month, field, formatTZS(parseTZS(value)));
    }
  };

  // Add computed fields so search + render stay consistent
  const computedVehicleData = useMemo(() => {
    return vehicleRows.map((row) => {
      const daysUsed = parseNumber(row.daysUsed);
      const rentalCost = parseTZS(row.rentalCost);
      const rentalCostThisMonth =
        String(row.daysUsed ?? '').trim() === ''
          ? ''
          : formatTZS(daysUsed * (rentalCost / 30));

      const startingMileage = parseNumber(row.startingMileage);
      const endingMileage = parseNumber(row.endingMileage);
      const kmsTraveled =
        String(row.endingMileage ?? '').trim() === ''
          ? ''
          : formatNumber(Math.max(0, endingMileage - startingMileage));

      const fuelLitres = parseNumber(row.fuel);
      const numericKms = parseNumber(kmsTraveled);
      const fuelConsumption =
        String(row.fuel ?? '').trim() === '' || String(kmsTraveled).trim() === '' || numericKms <= 0
          ? ''
          : `${(fuelLitres / numericKms * 100).toFixed(1)} L/100km`;

      return {
        ...row,
        rentalCostThisMonth,
        kmsTraveled,
        fuelConsumption,
      };
    });
  }, [vehicleRows]);

  // Get unique month/year values for dropdown, sorted from newest to oldest
  const monthYearOptions = useMemo(() => {
    const uniqueMonths = [...new Set(computedVehicleData.map(row => row.month))];
    // Sort months from newest to oldest
    const sortedMonths = uniqueMonths.sort((a, b) => {
      // Parse month and year from strings like "December 2025"
      const parseDate = (str) => {
        const months = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4,
          'May': 5, 'June': 6, 'July': 7, 'August': 8,
          'September': 9, 'October': 10, 'November': 11, 'December': 12
        };
        const parts = str.split(' ');
        const month = months[parts[0]];
        const year = parseInt(parts[1]);
        return new Date(year, month - 1);
      };
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      // Sort descending (newest first)
      return dateB - dateA;
    });
    return sortedMonths;
  }, [computedVehicleData]);

  // Default to the real current month (e.g., "January 2026") if available,
  // otherwise fall back to the newest available option
  useEffect(() => {
    if (!selectedMonthYear && monthYearOptions.length > 0) {
      const monthNames = [
        'January','February','March','April','May','June',
        'July','August','September','October','November','December'
      ];
      const now = new Date();
      const currentLabel = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      const initial = monthYearOptions.includes(currentLabel)
        ? currentLabel
        : monthYearOptions[0];
      setSelectedMonthYear(initial);
    }
  }, [monthYearOptions, selectedMonthYear]);

  // Get unique car rental options for the selected month
  const carRentalOptions = useMemo(() => {
    let vehiclesForMonth = computedVehicleData;
    if (selectedMonthYear) {
      vehiclesForMonth = vehiclesForMonth.filter(row => row.month === selectedMonthYear);
    }
    const uniqueRentals = [...new Set(vehiclesForMonth.map(row => row.carRental))].filter(Boolean).sort();
    return uniqueRentals;
  }, [computedVehicleData, selectedMonthYear]);

  // Filter vehicle data based on search term, month/year, and car rental
  const filteredVehicleData = useMemo(() => {
    let filtered = computedVehicleData;

    // Filter by month/year
    if (selectedMonthYear) {
      filtered = filtered.filter(row => row.month === selectedMonthYear);
    }

    // Filter by car rental
    if (selectedCarRental) {
      filtered = filtered.filter(row => row.carRental === selectedCarRental);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(row => {
        return Object.values(row).some(value => 
          String(value).toLowerCase().includes(term)
        );
      });
    }

    return filtered;
  }, [computedVehicleData, searchTerm, selectedMonthYear, selectedCarRental]);

  // Count vehicles based on all active filters
  const vehicleCountInMonth = useMemo(() => {
    return filteredVehicleData.length;
  }, [filteredVehicleData]);

  // Get previous month label
  const getPreviousMonth = (currentMonthYear) => {
    if (!currentMonthYear) return null;
    const monthNames = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    const parts = currentMonthYear.split(' ');
    const monthName = parts[0];
    const year = parseInt(parts[1]);
    const monthIndex = monthNames.indexOf(monthName);
    
    if (monthIndex === -1) return null;
    
    let prevMonthIndex = monthIndex - 1;
    let prevYear = year;
    
    if (prevMonthIndex < 0) {
      prevMonthIndex = 11; // December
      prevYear = year - 1;
    }
    
    return `${monthNames[prevMonthIndex]} ${prevYear}`;
  };

  // Compare fuel consumption with previous month
  const getFuelConsumptionComparison = (currentRow) => {
    const prevMonth = getPreviousMonth(currentRow.month);
    if (!prevMonth) return null;

    const prevMonthRow = computedVehicleData.find(
      row => row.regNo === currentRow.regNo && row.month === prevMonth
    );

    if (!prevMonthRow || !prevMonthRow.fuelConsumption) return null;

    const currentConsumption = parseFloat(currentRow.fuelConsumption?.replace(' L/100km', '') || 0);
    const prevConsumption = parseFloat(prevMonthRow.fuelConsumption.replace(' L/100km', '') || 0);

    if (isNaN(currentConsumption) || isNaN(prevConsumption)) return null;

    const diff = currentConsumption - prevConsumption;
    const threshold = 0.1; // Small threshold for "constant"

    if (Math.abs(diff) < threshold) {
      return 'constant';
    } else if (diff < 0) {
      return 'dropped'; // Decreased consumption
    } else {
      return 'increased'; // Increased consumption
    }
  };

  // Calculate subtotals for filtered vehicle data
  const subtotals = useMemo(() => {
    const totals = {
      rentalCost: 0,
      rentalCostThisMonth: 0,
      kmsTraveled: 0,
      fuel: 0,
      fuelCost: 0
    };

    filteredVehicleData.forEach(row => {
      totals.rentalCost += parseTZS(row.rentalCost);
      if (row.rentalCostThisMonth && row.rentalCostThisMonth.trim() !== '') {
        totals.rentalCostThisMonth += parseTZS(row.rentalCostThisMonth);
      }
      if (row.kmsTraveled && row.kmsTraveled.trim() !== '') {
        totals.kmsTraveled += parseNumber(row.kmsTraveled);
      }
      if (row.fuel && row.fuel.trim() !== '') {
        totals.fuel += parseNumber(row.fuel);
      }
      if (row.fuelCost && row.fuelCost.trim() !== '') {
        totals.fuelCost += parseTZS(row.fuelCost);
      }
    });

    return {
      rentalCost: formatTZS(totals.rentalCost),
      rentalCostThisMonth: totals.rentalCostThisMonth > 0 ? formatTZS(totals.rentalCostThisMonth) : '',
      kmsTraveled: totals.kmsTraveled > 0 ? formatNumber(totals.kmsTraveled) : '',
      fuel: totals.fuel > 0 ? formatNumber(totals.fuel) : '',
      fuelCost: totals.fuelCost > 0 ? formatTZS(totals.fuelCost) : ''
    };
  }, [filteredVehicleData]);

  // Get vehicles for current month (for return modal)
  const currentMonthVehicles = useMemo(() => {
    return computedVehicleData.filter(row => row.month === selectedMonthYear || row.month === currentMonthYearLabel);
  }, [computedVehicleData, selectedMonthYear, currentMonthYearLabel]);

  const handleReturnVehicle = () => {
    setShowReturnModal(true);
    setSelectedVehicleForReturn(null);
    setReturnDate('');
    setReturnReason('');
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicleForReturn(vehicle);
    // Set today's date as default
    const today = new Date().toISOString().split('T')[0];
    setReturnDate(today);
  };

  const handleSubmitReturn = () => {
    if (!selectedVehicleForReturn || !returnDate || !returnReason.trim()) {
      alert('Please select a vehicle, enter return date, and provide a reason.');
      return;
    }
    
    // Generate unique ID
    const maxId = returnedVehicles.length > 0 ? Math.max(...returnedVehicles.map(v => v.id)) : 0;
    
    // Create new returned vehicle record
    const newReturnedVehicle = {
      id: maxId + 1,
      regNo: selectedVehicleForReturn.regNo,
      vehicleType: selectedVehicleForReturn.vehicleType,
      carRental: selectedVehicleForReturn.carRental,
      vehicleUser: selectedVehicleForReturn.vehicleUser,
      returnDate: returnDate,
      returnReason: returnReason.trim(),
      status: 'Returned'
    };
    
    // Add to returned vehicles list (add at the beginning to show newest first)
    setReturnedVehicles(prev => [newReturnedVehicle, ...prev]);
    
    // Automatically switch to maintenance tab and returned vehicles sub-tab to show the new entry
    setActiveTab('maintenance');
    setMaintenanceSubTab('returned');
    
    alert(`Vehicle ${selectedVehicleForReturn.regNo} marked as returned on ${returnDate}`);
    
    // Reset and close modal
    setShowReturnModal(false);
    setSelectedVehicleForReturn(null);
    setReturnDate('');
    setReturnReason('');
  };

  // Invoice handling functions
  const handleOpenInvoiceModal = () => {
    // Generate next invoice number
    const currentYear = new Date().getFullYear().toString().slice(-2);
    const lastInvoice = invoices[0]?.invoiceNo || `FA${currentYear}/000000`;
    const lastNumber = parseInt(lastInvoice.split('/')[1]) || 0;
    const nextNumber = (lastNumber + 1).toString().padStart(6, '0');
    const nextInvoiceNo = `FA${currentYear}/${nextNumber}`;
    
    // Get current month/year for default date
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const now = new Date();
    const defaultDate = `${monthNames[now.getMonth()]} - ${now.getFullYear()}`;
    
    setInvoiceForm({
      invoiceNo: nextInvoiceNo,
      invoiceDate: defaultDate,
      project: 'BTS',
      fuelAmount: '',
      managementFee: '',
      vat: '',
      po: '',
      paymentStatus: 'Pending',
      datePaid: '',
      comment: ''
    });
    setShowInvoiceModal(true);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setInvoiceForm({
      invoiceNo: '',
      invoiceDate: '',
      project: 'BTS',
      fuelAmount: '',
      managementFee: '',
      vat: '',
      po: '',
      paymentStatus: 'Pending',
      datePaid: '',
      comment: ''
    });
  };

  const handleInvoiceFormChange = (field, value) => {
    setInvoiceForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate VAT when management fee changes: VAT = Management Fee × 0.18
      if (field === 'managementFee') {
        const management = parseTZS(value || '0');
        const calculatedVAT = management * 0.18;
        updated.vat = formatNumber(calculatedVAT);
      }
      
      return updated;
    });
  };

  const handleSubmitInvoice = async () => {
    // Validate required fields
    if (!invoiceForm.invoiceNo || !invoiceForm.invoiceDate || !invoiceForm.fuelAmount || !invoiceForm.po) {
      alert('Please fill in all required fields (Invoice No, Invoice Date, Fuel Amount, and PO).');
      return;
    }

    const fuel = parseTZS(invoiceForm.fuelAmount);
    const management = parseTZS(invoiceForm.managementFee || '0');
    const vat = parseTZS(invoiceForm.vat || '0');
    const totalAmount = calcInvoiceTotal(fuel, management, vat);

    setInvoiceSubmitting(true);
    setInvoicesError('');
    try {
      const minSort = invoices.reduce((min, row) => Math.min(min, Number(row.sortOrder) || 0), 0);
      const created = await createTotalEnergiesInvoice({
        invoiceNo: invoiceForm.invoiceNo,
        invoiceDate: invoiceForm.invoiceDate,
        project: invoiceForm.project || BTS_PROJECT,
        fuelAmount: fuel,
        managementFee: management,
        vat,
        totalAmount,
        po: invoiceForm.po,
        paymentStatus: invoiceForm.paymentStatus,
        datePaid: invoiceForm.paymentStatus === 'Paid' ? invoiceForm.datePaid || '-' : '-',
        comment: invoiceForm.comment || '',
        sortOrder: minSort - 10,
      });
      setInvoices((prev) => [created, ...prev]);
      alert(`Invoice ${created.invoiceNo} has been recorded successfully!`);
      handleCloseInvoiceModal();
    } catch (err) {
      console.error(err);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(err) || 'Failed to save invoice.');
      alert(getTotalEnergiesInvoiceErrorMessage(err) || 'Failed to save invoice.');
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  // Calculate invoice summary statistics
  const invoiceSummary = useMemo(() => {
    const totalAmount = btsInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const paidInvoices = btsInvoices.filter((inv) => inv.paymentStatus === 'Paid');
    const processedInvoices = btsInvoices.filter((inv) => inv.paymentStatus === 'Processed');
    const pendingInvoices = btsInvoices.filter((inv) => inv.paymentStatus === 'Pending');
    const paidAmount = paidInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const processedAmount = processedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);

    return {
      totalAmount,
      paidCount: paidInvoices.length,
      paidAmount,
      processedCount: processedInvoices.length,
      processedAmount,
      pendingCount: pendingInvoices.length,
      pendingAmount,
    };
  }, [btsInvoices]);

  return (
    <div className="bts-project-page">
      <div className="page-header">
        <div className="breadcrumb">
          <Link to="/sagemcom" className="breadcrumb-item clickable">Sagemcom</Link>
          <span className="breadcrumb-separator">›</span>
          <Link to="/sagemcom/fleet-management" className="breadcrumb-item clickable">Fleet Management</Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">BTS</span>
        </div>
        <div className="header-title-section">
          <h1>BTS</h1>
          <span className="project-units-badge">24 Units</span>
        </div>
        <p>Base Station</p>
      </div>

      <div className="bts-nav-bar">
        <div 
          className={`bts-nav-item ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </div>
        <Link
          to="/sagemcom/fleet-management/vehicle-report"
          className="bts-nav-item"
        >
          Vehicle Monthly Report
        </Link>
        <div 
          className={`bts-nav-item ${activeTab === 'fuel' ? 'active' : ''}`}
          onClick={() => setActiveTab('fuel')}
        >
          Project Monthly Fuel Report
        </div>
        <div 
          className={`bts-nav-item ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Maintenance Record
        </div>
        <div 
          className={`bts-nav-item ${activeTab === 'po' ? 'active' : ''}`}
          onClick={() => setActiveTab('po')}
        >
          PO Tracking
        </div>
        <div 
          className={`bts-nav-item ${activeTab === 'invoice' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoice')}
        >
          Invoice Tracking
        </div>
      </div>

      <div className="bts-content-area">
        {activeTab === 'overview' && (
            <div className="bts-stats-grid">
              <div className="bts-stat-card maintenance-card">
                <div className="bts-stat-icon-wrapper">
                  <FaWrench className="bts-stat-icon wrench" />
                </div>
                <div className="bts-stat-content">
                  <p className="bts-stat-number">2</p>
                <p className="bts-stat-label">Maintenance Units</p>
                </div>
                <div className="bts-stat-trend orange">↗</div>
              </div>
              
            <div className="bts-stat-card new-vehicle-card">
                <div className="bts-stat-icon-wrapper">
                <FaPlus className="bts-stat-icon new" />
                </div>
                <div className="bts-stat-content">
                <p className="bts-stat-number">3</p>
                <p className="bts-stat-label">New Vehicle This Month</p>
              </div>
              <div className="bts-stat-trend green">↗</div>
            </div>

            <div className="bts-stat-card returned-card">
              <div className="bts-stat-icon-wrapper">
                <FaCar className="bts-stat-icon returned" />
                </div>
              <div className="bts-stat-content">
                <p className="bts-stat-number">1</p>
                <p className="bts-stat-label">Returned Vehicle This Month</p>
                </div>
              <div className="bts-stat-trend blue">↗</div>
                </div>
            
            <div className="bts-stat-card total-card">
              <div className="bts-stat-icon-wrapper">
                <FaCheckCircle className="bts-stat-icon total" />
                </div>
              <div className="bts-stat-content">
                <p className="bts-stat-number">24</p>
                <p className="bts-stat-label">Number of All Vehicles</p>
              </div>
              <div className="bts-stat-trend neutral">—</div>
            </div>
          </div>
        )}

        {activeTab === 'vehicle' && (
          <div className="bts-tab-content">
            <div className="report-header">
              <FaCar className="report-icon" />
              <div className="report-title-wrapper">
                <div className="report-title-row">
                  <h3>Vehicle Monthly Report</h3>
                  <span className="vehicle-count-badge">
                    {vehicleCountInMonth} {vehicleCountInMonth === 1 ? 'Vehicle' : 'Vehicles'}
                  </span>
                </div>
                <span className="report-month-year">
                  {selectedMonthYear || currentMonthYearLabel}
                </span>
              </div>
            </div>
            
            <div className="vehicle-month-filter">
              <FaCalendarAlt className="filter-icon" />
              <div className="dropdown-wrapper">
                <select
                  className="month-year-dropdown"
                  value={selectedMonthYear}
                  onChange={(e) => {
                    setSelectedMonthYear(e.target.value);
                    setSearchTerm('');
                    setSelectedCarRental('');
                  }}
                >
                  {monthYearOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="dropdown-arrow" />
                </div>
              </div>

            <div className="vehicle-controls-row">
              <div className="vehicle-search-container">
                <div className="vehicle-search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="vehicle-search-input"
                    placeholder="Search by vehicle type, rental, reg no, user, mileage, fuel, cost..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="car-rental-filter-container">
                <div className="dropdown-wrapper">
                  <select
                    className="car-rental-dropdown"
                    value={selectedCarRental}
                    onChange={(e) => setSelectedCarRental(e.target.value)}
                  >
                    <option value="">All Car Rentals</option>
                    {carRentalOptions.map((rental) => (
                      <option key={rental} value={rental}>
                        {rental}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown className="dropdown-arrow" />
                </div>
              </div>

              <div className="return-vehicle-container">
                <button className="return-vehicle-btn" onClick={handleReturnVehicle}>
                  <FaUndo className="return-icon" />
                  <span>Return Vehicle</span>
                </button>
              </div>
            </div>

            <div className="vehicle-report-table-wrapper">
              <table className="vehicle-report-table">
                <thead>
                  {/* Subtotal Row */}
                  <tr className="subtotal-row">
                    <th colSpan="6" className="subtotal-label">Subtotal</th>
                    <th className="subtotal-value">{subtotals.rentalCost}</th>
                    <th className="subtotal-value">{subtotals.rentalCostThisMonth}</th>
                    <th></th>
                    <th></th>
                    <th className="subtotal-value">{subtotals.kmsTraveled}</th>
                    <th className="subtotal-value">{subtotals.fuel}</th>
                    <th className="subtotal-value">{subtotals.fuelCost}</th>
                    <th></th>
                  </tr>
                  <tr>
                    <th>Month</th>
                    <th>Vehicle Type</th>
                    <th>Car Rental</th>
                    <th>Reg No</th>
                    <th>Vehicle User</th>
                    <th>No of days Used</th>
                    <th>Rental Cost</th>
                    <th>Rental Cost This Month</th>
                    <th>Starting Mileage</th>
                    <th>Ending Mileage</th>
                    <th>KMs Traveled</th>
                    <th>Fuel (L)</th>
                    <th>Fuel Cost</th>
                    <th>Fuel Consumption</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicleData.length > 0 ? (
                    filteredVehicleData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.month}</td>
                        <td>{row.vehicleType}</td>
                        <td>{row.carRental}</td>
                        <td>{row.regNo}</td>
                        <td>{row.vehicleUser}</td>
                        <td>
                          <input
                            className="vehicle-inline-input"
                            value={row.daysUsed}
                            inputMode="numeric"
                            onChange={(e) => updateVehicleCell(row.regNo, row.month, 'daysUsed', e.target.value)}
                            onBlur={(e) => finalizeVehicleCell(row.regNo, row.month, 'daysUsed', e.target.value)}
                          />
                        </td>
                        <td>{row.rentalCost}</td>
                        <td>{row.rentalCostThisMonth}</td>
                        <td>{row.startingMileage}</td>
                        <td>
                          <input
                            className="vehicle-inline-input"
                            value={row.endingMileage}
                            inputMode="numeric"
                            onChange={(e) => updateVehicleCell(row.regNo, row.month, 'endingMileage', e.target.value)}
                            onBlur={(e) => finalizeVehicleCell(row.regNo, row.month, 'endingMileage', e.target.value)}
                          />
                        </td>
                        <td className={parseNumber(row.kmsTraveled) <= 0 ? 'negative-kms' : ''}>{row.kmsTraveled}</td>
                        <td>
                          <input
                            className="vehicle-inline-input"
                            value={row.fuel}
                            inputMode="numeric"
                            onChange={(e) => updateVehicleCell(row.regNo, row.month, 'fuel', e.target.value)}
                            onBlur={(e) => finalizeVehicleCell(row.regNo, row.month, 'fuel', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="vehicle-inline-input"
                            value={row.fuelCost}
                            inputMode="numeric"
                            onChange={(e) => updateVehicleCell(row.regNo, row.month, 'fuelCost', e.target.value)}
                            onBlur={(e) => finalizeVehicleCell(row.regNo, row.month, 'fuelCost', e.target.value)}
                          />
                        </td>
                        <td className="fuel-consumption-cell">
                          <span>{row.fuelConsumption}</span>
                          {(() => {
                            const comparison = getFuelConsumptionComparison(row);
                            if (!comparison) return null;
                            
                            if (comparison === 'dropped') {
                              return <span className="fuel-arrow red-arrow-down">↓</span>;
                            } else if (comparison === 'increased') {
                              return <span className="fuel-arrow red-arrow-up">↑</span>;
                            } else if (comparison === 'constant') {
                              return <span className="fuel-arrow yellow-dash">—</span>;
                            }
                            return null;
                          })()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="14" style={{ textAlign: 'center', padding: '2rem' }}>
                        No results found for "{searchTerm}"
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'fuel' && (
          <div className="bts-tab-content fuel-report">
            <div className="fuel-report-header">
              <div className="fuel-header-left">
                <div className="fuel-header-title">
                  <FaGasPump className="fuel-icon" />
                  <div>
                    <h3>Project Monthly Fuel Report</h3>
                    <p className="fuel-subtitle">Track fuel consumption and costs across all projects</p>
                  </div>
                </div>
              </div>
              <button className="record-invoice-btn" onClick={handleOpenInvoiceModal}>
                <FaPlus />
                <span>Record New Invoice</span>
              </button>
            </div>

            <div className="fuel-summary-cards">
              <div className="fuel-summary-card blue-card">
                <div className="card-icon-wrapper">
                  <FaDollarSign className="card-icon" />
                </div>
                <div className="card-content">
                  <p className="card-label">Total Invoices Amount</p>
                  <p className="card-amount">{formatTZS(invoiceSummary.totalAmount)}</p>
                </div>
              </div>

              <div className="fuel-summary-card green-card">
                <div className="card-icon-wrapper">
                  <FaCheckCircle className="card-icon" />
                </div>
                <div className="card-content">
                  <p className="card-label">Payment Status</p>
                  <div className="payment-details">
                    <p>• Paid: {formatTZS(invoiceSummary.paidAmount)} ({invoiceSummary.paidCount})</p>
                    <p>• Processed: {formatTZS(invoiceSummary.processedAmount)} ({invoiceSummary.processedCount})</p>
                    <p>• Pending: {formatTZS(invoiceSummary.pendingAmount)} ({invoiceSummary.pendingCount})</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="fuel-chart-section">
              <div className="chart-header">
                <div className="chart-title-section">
                  <FaChartLine className="chart-icon" />
                  <h4>Monthly Invoice Trends</h4>
                </div>
                <p className="chart-info">
                  Data Points: {btsInvoices.length} | Total Amount: {formatTZS(invoiceSummary.totalAmount)} | Loaded: {btsInvoices.length} records | Has More: No | Auto Loading: {invoicesLoading ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div className="chart-container">
                <div className="chart-y-axis">
                  <span>22.2M</span>
                  <span>18.5M</span>
                  <span>14.9M</span>
                  <span>11.2M</span>
                  <span>7.4M</span>
                  <span>3.8M</span>
                </div>
                <div className="chart-area">
                  <svg viewBox="0 0 800 300" className="trend-chart">
                    <defs>
                      <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(59, 130, 246, 0.3)" />
                        <stop offset="100%" stopColor="rgba(59, 130, 246, 0.05)" />
                      </linearGradient>
                    </defs>
                    
                    {/* Grid lines */}
                    <line x1="0" y1="50" x2="800" y2="50" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                    <line x1="0" y1="100" x2="800" y2="100" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                    <line x1="0" y1="150" x2="800" y2="150" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                    <line x1="0" y1="200" x2="800" y2="200" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                    <line x1="0" y1="250" x2="800" y2="250" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                    
                    {/* Area under the line */}
                    <path
                      d="M 0,240 L 35,180 L 70,120 L 105,140 L 140,100 L 175,80 L 210,70 L 245,90 L 280,85 L 315,95 L 350,110 L 385,100 L 420,105 L 455,115 L 490,120 L 525,110 L 560,115 L 595,108 L 630,112 L 665,118 L 700,105 L 735,110 L 770,102 L 800,108 L 800,300 L 0,300 Z"
                      fill="url(#chartGradient)"
                    />
                    
                    {/* Trend line */}
                    <path
                      d="M 0,240 L 35,180 L 70,120 L 105,140 L 140,100 L 175,80 L 210,70 L 245,90 L 280,85 L 315,95 L 350,110 L 385,100 L 420,105 L 455,115 L 490,120 L 525,110 L 560,115 L 595,108 L 630,112 L 665,118 L 700,105 L 735,110 L 770,102 L 800,108"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />
                    
                    {/* Data points */}
                    <circle cx="0" cy="240" r="4" fill="#3b82f6"/>
                    <circle cx="35" cy="180" r="4" fill="#3b82f6"/>
                    <circle cx="70" cy="120" r="4" fill="#3b82f6"/>
                    <circle cx="105" cy="140" r="4" fill="#3b82f6"/>
                    <circle cx="140" cy="100" r="4" fill="#3b82f6"/>
                    <circle cx="175" cy="80" r="4" fill="#3b82f6"/>
                    <circle cx="210" cy="70" r="4" fill="#3b82f6"/>
                    <circle cx="245" cy="90" r="4" fill="#3b82f6"/>
                    <circle cx="280" cy="85" r="4" fill="#3b82f6"/>
                    <circle cx="315" cy="95" r="4" fill="#3b82f6"/>
                    <circle cx="350" cy="110" r="4" fill="#3b82f6"/>
                    <circle cx="385" cy="100" r="4" fill="#3b82f6"/>
                    <circle cx="420" cy="105" r="4" fill="#3b82f6"/>
                    <circle cx="455" cy="115" r="4" fill="#3b82f6"/>
                    <circle cx="490" cy="120" r="4" fill="#3b82f6"/>
                    <circle cx="525" cy="110" r="4" fill="#3b82f6"/>
                    <circle cx="560" cy="115" r="4" fill="#3b82f6"/>
                    <circle cx="595" cy="108" r="4" fill="#3b82f6"/>
                    <circle cx="630" cy="112" r="4" fill="#3b82f6"/>
                    <circle cx="665" cy="118" r="4" fill="#3b82f6"/>
                    <circle cx="700" cy="105" r="4" fill="#3b82f6"/>
                    <circle cx="735" cy="110" r="4" fill="#3b82f6"/>
                    <circle cx="770" cy="102" r="4" fill="#3b82f6"/>
                    <circle cx="800" cy="108" r="4" fill="#3b82f6"/>
                  </svg>
                  <div className="chart-x-axis">
                    <span>Oct '23</span>
                    <span>Nov '23</span>
                    <span>Dec '23</span>
                    <span>Jan '24</span>
                    <span>Feb '24</span>
                    <span>Mar '24</span>
                    <span>Apr '24</span>
                    <span>May '24</span>
                    <span>Jun '24</span>
                    <span>Jul '24</span>
                    <span>Aug '24</span>
                    <span>Sep '24</span>
                    <span>Oct '24</span>
                    <span>Nov '24</span>
                    <span>Dec '24</span>
                    <span>Jan '25</span>
                    <span>Feb '25</span>
                    <span>Mar '25</span>
                    <span>Apr '25</span>
                    <span>May '25</span>
                    <span>Jun '25</span>
                    <span>Jul '25</span>
                    <span>Aug '25</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="invoices-table-section">
              <h4>Invoice Details</h4>
              <div className="table-wrapper">
                <table className="invoices-table">
                  <thead>
                    <tr>
                      <th>Invoice No</th>
                      <th>Invoice Date</th>
                      <th>Project</th>
                      <th>Fuel amount</th>
                      <th>Management fee</th>
                      <th>VAT</th>
                      <th>Total invoice amount</th>
                      <th>PO</th>
                      <th>Payment status</th>
                      <th>Date paid</th>
                      <th>Comment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesLoading ? (
                      <tr>
                        <td colSpan={12}>Loading fuel invoices…</td>
                      </tr>
                    ) : btsInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={12}>No BTS fuel invoices yet.</td>
                      </tr>
                    ) : (
                    btsInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoiceNo}</td>
                        <td>{invoice.invoiceDate}</td>
                        <td>{invoice.project}</td>
                        <td>TSh {formatNumber(invoice.fuelAmount)}</td>
                        <td>TSh {formatNumber(invoice.managementFee)}</td>
                        <td>TSh {formatNumber(invoice.vat)}</td>
                        <td>TSh {formatNumber(invoice.totalAmount)}</td>
                        <td>{invoice.po}</td>
                        <td>
                          <span className={`status-badge ${invoice.paymentStatus.toLowerCase()}`}>
                            {invoice.paymentStatus}
                          </span>
                        </td>
                        <td>{invoice.datePaid}</td>
                        <td>{invoice.comment || '-'}</td>
                        <td><button className="action-icon"><FaEdit /></button></td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="bts-tab-content">
            <div className="maintenance-header">
              <h3>Maintenance Record</h3>
            </div>
            
            {/* Maintenance Sub-tabs */}
            <div className="maintenance-sub-tabs">
              <button
                className={`maintenance-sub-tab ${maintenanceSubTab === 'record' ? 'active' : ''}`}
                onClick={() => setMaintenanceSubTab('record')}
              >
                Maintenance Record
              </button>
              <button
                className={`maintenance-sub-tab ${maintenanceSubTab === 'returned' ? 'active' : ''}`}
                onClick={() => setMaintenanceSubTab('returned')}
              >
                Returned Vehicles
              </button>
            </div>

            {/* Maintenance Record Tab Content */}
            {maintenanceSubTab === 'record' && (
              <div className="maintenance-record-section">
                <p className="placeholder-text">Maintenance records and history will be displayed here.</p>
              </div>
            )}

            {/* Returned Vehicles Tab Content */}
            {maintenanceSubTab === 'returned' && (
              <div className="returned-vehicles-section">
                <div className="returned-vehicles-table-section">
                  <div className="table-wrapper">
                    <table className="returned-vehicles-table">
                      <thead>
                        <tr>
                          <th>Registration No</th>
                          <th>Vehicle Type</th>
                          <th>Car Rental</th>
                          <th>Vehicle User</th>
                          <th>Return Date</th>
                          <th>Reason for Return</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnedVehicles.map((vehicle) => (
                          <tr key={vehicle.id}>
                            <td>{vehicle.regNo}</td>
                            <td>{vehicle.vehicleType}</td>
                            <td>{vehicle.carRental}</td>
                            <td>{vehicle.vehicleUser}</td>
                            <td>{vehicle.returnDate}</td>
                            <td>{vehicle.returnReason}</td>
                            <td>
                              <span className={`status-badge ${vehicle.status.toLowerCase()}`}>
                                {vehicle.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'po' && (
          <div className="bts-tab-content">
            <div className="po-tracking-header">
              <h3>PO Tracking</h3>
              <button className="record-po-btn" onClick={handleOpenPOModal}>
                <FaPlus />
                <span>Record PO</span>
              </button>
            </div>
            
            <div className="po-tracking-table-section">
              <div className="table-wrapper">
                <table className="po-tracking-table">
                  <thead>
                    <tr>
                      <th>PO Number</th>
                      <th>Project</th>
                      <th>PO Amount</th>
                      <th>Total Amount Invoiced</th>
                      <th>PO Balance</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poTracking.map((po) => {
                      const poBalance = po.poAmount - po.totalAmountInvoiced;
                      return (
                        <tr key={po.id}>
                          <td>{po.poNumber}</td>
                          <td>{po.project}</td>
                          <td>TZS {formatNumber(po.poAmount)}</td>
                          <td>TZS {formatNumber(po.totalAmountInvoiced)}</td>
                          <td className={poBalance < 0 ? 'negative-balance' : ''}>TZS {formatNumber(poBalance)}</td>
                          <td>{po.comment}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoice' && (
          <div className="bts-tab-content">
            <div className="invoice-tracking-header">
              <h3>Invoice Tracking</h3>
              <button className="record-invoice-tracking-btn" onClick={handleOpenInvoiceTrackingModal}>
                <FaPlus />
                <span>Record Invoice</span>
              </button>
            </div>
            
            <div className="invoice-tracking-table-section">
              <div className="table-wrapper">
                <table className="invoice-tracking-table">
                  <thead>
                    <tr>
                      <th>Invoice Number</th>
                      <th>PO Number</th>
                      <th>Project</th>
                      <th>Invoice Amount</th>
                      <th>Date Invoiced</th>
                      <th>Payment Status</th>
                      <th>Date Paid</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceTracking.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoiceNumber}</td>
                        <td>{invoice.poNumber}</td>
                        <td>{invoice.project}</td>
                        <td>TZS {formatNumber(invoice.invoiceAmount)}</td>
                        <td>{invoice.dateInvoiced}</td>
                        <td>
                          <span className={`status-badge ${invoice.paymentStatus.toLowerCase()}`}>
                            {invoice.paymentStatus}
                          </span>
                        </td>
                        <td>{invoice.datePaid || '-'}</td>
                        <td>{invoice.comment || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Return Vehicle Modal */}
        {showReturnModal && (
          <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Return Vehicle</h3>
                <button className="modal-close-btn" onClick={() => setShowReturnModal(false)}>
                  <FaTimes />
                </button>
              </div>
              
              <div className="modal-body">
                {!selectedVehicleForReturn ? (
                  <div className="vehicle-selection-section">
                    <h4>Select a Vehicle to Return</h4>
                    <p className="modal-subtitle">Current Month: {selectedMonthYear || currentMonthYearLabel}</p>
                    {currentMonthVehicles.length > 0 ? (
                      <div className="vehicle-list">
                        {currentMonthVehicles.map((vehicle, index) => (
                          <div 
                            key={index} 
                            className="vehicle-list-item"
                            onClick={() => handleVehicleSelect(vehicle)}
                          >
                            <div className="vehicle-item-info">
                              <span className="vehicle-reg">{vehicle.regNo}</span>
                              <span className="vehicle-details">
                                {vehicle.vehicleType} • {vehicle.carRental} • {vehicle.vehicleUser}
                              </span>
                            </div>
                            <FaChevronDown className="vehicle-arrow" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-vehicles-message">No vehicles found for the current month.</p>
                    )}
                  </div>
                ) : (
                  <div className="return-form-section">
                    <div className="selected-vehicle-info">
                      <h4>Selected Vehicle</h4>
                      <div className="selected-vehicle-details">
                        <div><strong>Registration:</strong> {selectedVehicleForReturn.regNo}</div>
                        <div><strong>Type:</strong> {selectedVehicleForReturn.vehicleType}</div>
                        <div><strong>Rental:</strong> {selectedVehicleForReturn.carRental}</div>
                        <div><strong>User:</strong> {selectedVehicleForReturn.vehicleUser}</div>
                      </div>
                      <button 
                        className="change-vehicle-btn"
                        onClick={() => {
                          setSelectedVehicleForReturn(null);
                          setReturnDate('');
                          setReturnReason('');
                        }}
                      >
                        Change Vehicle
                      </button>
                    </div>

                    <div className="form-group">
                      <label htmlFor="return-date">Date Returned *</label>
                      <input
                        type="date"
                        id="return-date"
                        className="form-input"
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="return-reason">Reason for Return *</label>
                      <textarea
                        id="return-reason"
                        className="form-textarea"
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        placeholder="Enter the reason for returning this vehicle..."
                        rows="4"
                        required
                      />
                    </div>

                    <div className="modal-actions">
                      <button 
                        className="cancel-btn"
                        onClick={() => {
                          setShowReturnModal(false);
                          setSelectedVehicleForReturn(null);
                          setReturnDate('');
                          setReturnReason('');
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="submit-btn"
                        onClick={handleSubmitReturn}
                      >
                        Submit Return
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Record Invoice Modal */}
        {showInvoiceModal && (
          <div className="modal-overlay" onClick={handleCloseInvoiceModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Record New Invoice</h3>
                <button className="modal-close-btn" onClick={handleCloseInvoiceModal}>
                  <FaTimes />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="return-form-section">
                  <div className="form-group">
                    <label htmlFor="invoice-no">Invoice No *</label>
                    <input
                      type="text"
                      id="invoice-no"
                      className="form-input"
                      value={invoiceForm.invoiceNo}
                      onChange={(e) => handleInvoiceFormChange('invoiceNo', e.target.value)}
                      placeholder="e.g., FA25/160101"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-date">Invoice Date *</label>
                    <input
                      type="text"
                      id="invoice-date"
                      className="form-input"
                      value={invoiceForm.invoiceDate}
                      onChange={(e) => handleInvoiceFormChange('invoiceDate', e.target.value)}
                      placeholder="e.g., August - 2025"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="project">Project</label>
                    <input
                      type="text"
                      id="project"
                      className="form-input"
                      value={invoiceForm.project}
                      onChange={(e) => handleInvoiceFormChange('project', e.target.value)}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="fuel-amount">Fuel Amount (TZS) *</label>
                    <input
                      type="text"
                      id="fuel-amount"
                      className="form-input"
                      value={invoiceForm.fuelAmount}
                      onChange={(e) => handleInvoiceFormChange('fuelAmount', e.target.value)}
                      placeholder="e.g., 21,656,658"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="management-fee">Management Fee (TZS)</label>
                    <input
                      type="text"
                      id="management-fee"
                      className="form-input"
                      value={invoiceForm.managementFee}
                      onChange={(e) => handleInvoiceFormChange('managementFee', e.target.value)}
                      placeholder="e.g., 128,472"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="vat">VAT (TZS) <span style={{ fontSize: '0.85rem', color: '#718096', fontWeight: 'normal' }}>(Auto-calculated: Management Fee × 0.18)</span></label>
                    <input
                      type="text"
                      id="vat"
                      className="form-input"
                      value={invoiceForm.vat}
                      onChange={(e) => handleInvoiceFormChange('vat', e.target.value)}
                      placeholder="Auto-calculated from Management Fee"
                      readOnly
                      style={{ backgroundColor: '#f7fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="total-amount">Total Invoice Amount (TZS)</label>
                    <input
                      type="text"
                      id="total-amount"
                      className="form-input"
                      value={formatTZS(
                        parseTZS(invoiceForm.fuelAmount || '0') + 
                        parseTZS(invoiceForm.managementFee || '0') + 
                        parseTZS(invoiceForm.vat || '0')
                      )}
                      readOnly
                      style={{ backgroundColor: '#f7fafc', fontWeight: '600' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="po">PO *</label>
                    <input
                      type="text"
                      id="po"
                      className="form-input"
                      value={invoiceForm.po}
                      onChange={(e) => handleInvoiceFormChange('po', e.target.value)}
                      placeholder="e.g., 660003077-2"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="payment-status">Payment Status</label>
                    <select
                      id="payment-status"
                      className="form-input"
                      value={invoiceForm.paymentStatus}
                      onChange={(e) => handleInvoiceFormChange('paymentStatus', e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processed">Processed</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>

                  {invoiceForm.paymentStatus === 'Paid' && (
                    <div className="form-group">
                      <label htmlFor="date-paid">Date Paid</label>
                      <input
                        type="text"
                        id="date-paid"
                        className="form-input"
                        value={invoiceForm.datePaid}
                        onChange={(e) => handleInvoiceFormChange('datePaid', e.target.value)}
                        placeholder="e.g., 21/08/2025"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="invoice-comment">Comment</label>
                    <textarea
                      id="invoice-comment"
                      className="form-textarea"
                      value={invoiceForm.comment}
                      onChange={(e) => handleInvoiceFormChange('comment', e.target.value)}
                      placeholder="Enter comment..."
                      rows="3"
                    />
                  </div>

                  <div className="modal-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleCloseInvoiceModal}
                    >
                      Cancel
                    </button>
                    <button 
                      className="submit-btn"
                      onClick={handleSubmitInvoice}
                    >
                      Record Invoice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Record PO Modal */}
        {showPOModal && (
          <div className="modal-overlay" onClick={handleClosePOModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Record New PO</h3>
                <button className="modal-close-btn" onClick={handleClosePOModal}>
                  <FaTimes />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="return-form-section">
                  <div className="form-group">
                    <label htmlFor="po-number">PO Number *</label>
                    <input
                      type="text"
                      id="po-number"
                      className="form-input"
                      value={poForm.poNumber}
                      onChange={(e) => handlePOFormChange('poNumber', e.target.value)}
                      placeholder="e.g., 660003077-2"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="po-project">Project</label>
                    <input
                      type="text"
                      id="po-project"
                      className="form-input"
                      value={poForm.project}
                      onChange={(e) => handlePOFormChange('project', e.target.value)}
                      readOnly
                      style={{ backgroundColor: '#f7fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="po-amount">PO Amount (TZS) *</label>
                    <input
                      type="text"
                      id="po-amount"
                      className="form-input"
                      value={poForm.poAmount}
                      onChange={(e) => handlePOFormChange('poAmount', e.target.value)}
                      placeholder="e.g., 25,000,000"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="po-balance">PO Balance (TZS)</label>
                    <input
                      type="text"
                      id="po-balance"
                      className="form-input"
                      value={formatTZS(parseTZS(poForm.poAmount || '0'))}
                      readOnly
                      style={{ backgroundColor: '#f7fafc', fontWeight: '600' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="po-comment">Comment</label>
                    <textarea
                      id="po-comment"
                      className="form-textarea"
                      value={poForm.comment}
                      onChange={(e) => handlePOFormChange('comment', e.target.value)}
                      placeholder="Enter comment..."
                      rows="3"
                    />
                  </div>

                  <div className="modal-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleClosePOModal}
                    >
                      Cancel
                    </button>
                    <button 
                      className="submit-btn"
                      onClick={handleSubmitPO}
                    >
                      Record PO
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Record Invoice Tracking Modal */}
        {showInvoiceTrackingModal && (
          <div className="modal-overlay" onClick={handleCloseInvoiceTrackingModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Record New Invoice</h3>
                <button className="modal-close-btn" onClick={handleCloseInvoiceTrackingModal}>
                  <FaTimes />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="return-form-section">
                  <div className="form-group">
                    <label htmlFor="invoice-number">Invoice Number *</label>
                    <input
                      type="text"
                      id="invoice-number"
                      className="form-input"
                      value={invoiceTrackingForm.invoiceNumber}
                      onChange={(e) => handleInvoiceTrackingFormChange('invoiceNumber', e.target.value)}
                      placeholder="e.g., INV-2025-001"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-po-number">PO Number *</label>
                    <select
                      id="invoice-po-number"
                      className="form-input"
                      value={invoiceTrackingForm.poNumber}
                      onChange={(e) => handleInvoiceTrackingFormChange('poNumber', e.target.value)}
                      required
                    >
                      <option value="">Select PO Number</option>
                      {poTracking.map((po) => (
                        <option key={po.id} value={po.poNumber}>
                          {po.poNumber}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-project">Project</label>
                    <input
                      type="text"
                      id="invoice-project"
                      className="form-input"
                      value={invoiceTrackingForm.project}
                      onChange={(e) => handleInvoiceTrackingFormChange('project', e.target.value)}
                      readOnly
                      style={{ backgroundColor: '#f7fafc', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-amount">Invoice Amount (TZS) *</label>
                    <input
                      type="text"
                      id="invoice-amount"
                      className="form-input"
                      value={invoiceTrackingForm.invoiceAmount}
                      onChange={(e) => handleInvoiceTrackingFormChange('invoiceAmount', e.target.value)}
                      placeholder="e.g., 21,808,255"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-date">Date Invoiced *</label>
                    <input
                      type="date"
                      id="invoice-date"
                      className="form-input"
                      value={invoiceTrackingForm.dateInvoiced}
                      onChange={(e) => handleInvoiceTrackingFormChange('dateInvoiced', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="invoice-payment-status">Payment Status</label>
                    <select
                      id="invoice-payment-status"
                      className="form-input"
                      value={invoiceTrackingForm.paymentStatus}
                      onChange={(e) => handleInvoiceTrackingFormChange('paymentStatus', e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>

                  {invoiceTrackingForm.paymentStatus === 'Paid' && (
                    <div className="form-group">
                      <label htmlFor="invoice-date-paid">Date Paid</label>
                      <input
                        type="date"
                        id="invoice-date-paid"
                        className="form-input"
                        value={invoiceTrackingForm.datePaid}
                        onChange={(e) => handleInvoiceTrackingFormChange('datePaid', e.target.value)}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="invoice-comment">Comment</label>
                    <textarea
                      id="invoice-comment"
                      className="form-textarea"
                      value={invoiceTrackingForm.comment}
                      onChange={(e) => handleInvoiceTrackingFormChange('comment', e.target.value)}
                      placeholder="Enter comment..."
                      rows="3"
                    />
                  </div>

                  <div className="modal-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleCloseInvoiceTrackingModal}
                    >
                      Cancel
                    </button>
                    <button 
                      className="submit-btn"
                      onClick={handleSubmitInvoiceTracking}
                    >
                      Record Invoice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BTSProjectPage;

